import React, { useState, useRef } from 'react';
import { AlertCircleIcon, CheckCircleIcon, DownloadIcon, FileTextIcon, UploadIcon, XCircleIcon } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { PageHero } from '../components/layout/PageHero';
import { FormStepper } from '../components/supplier/FormStepper';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { countries } from '../data/countries';
import { useScreenInit } from '../useScreenInit.js';
const FORMSUBMIT_ENDPOINT = 'https://formsubmit.co/procurement@aucommission.org';
const MAX_TOTAL_FILE_SIZE_MB = 10;
type DocKey = 'incorporation' | 'businessRegistration' | 'companyProfile' | 'proofOfAddress' | 'taxRegistration' | 'bankConfirmation' | 'governmentId' | 'passportPhoto';
type FormData = {
  companyName: string;
  parentCompany: string;
  address: string;
  phone: string;
  whatsappNumber: string;
  email: string;
  contactFullName: string;
  contactTitle: string;
  typeOfOrganisation: string;
  yearEstablished: string;
  registrationNumber: string;
  categories: string[];
  otherCategory: string;
  areaOfSpecialization: string;
  employeesTotal: string;
  employeesFullTime: string;
  employeesPartTime: string;
  authRepName: string;
  authRepTitle: string;
  authRepPhone: string;
  authRepEmail: string;
  accountName: string;
  bankName: string;
  bankAddress: string;
  accountNumber: string;
  iban: string;
  swiftBic: string;
  currency: string;
  auditedFinancials: string;
  turnoverCurrent: string;
  turnoverPrevious: string;
  bidSecurityAbility: string;
  bidSecurityType: string;
  complianceRepName: string;
  complianceRepNationality: string;
  complianceRepDOB: string;
  complianceRepPlaceOfBirth: string;
  complianceRepIdNumber: string;
  complianceRepCountryOfIssue: string;
  complianceRepFathersName: string;
  complianceRepMothersName: string;
  complianceRepAddress: string;
  complianceRepCountry: string;
  complianceRepPhone: string;
  complianceRepEmail: string;
  complianceRepPosition: string;
  confirmAccurate: boolean;
  agreeCodeOfConduct: boolean;
  understandFalseInfo: boolean;
  declarationName: string;
  declarationTitle: string;
  declarationDate: string;
  signature: string;
};
type TextKey = Exclude<keyof FormData, 'categories' | 'confirmAccurate' | 'agreeCodeOfConduct' | 'understandFalseInfo'>;
type DocumentDef = {
  key: DocKey;
  label: string;
  required: boolean;
  accept: string;
};
const emptyForm: FormData = {
  companyName: '',
  parentCompany: '',
  address: '',
  phone: '',
  whatsappNumber: '',
  email: '',
  contactFullName: '',
  contactTitle: '',
  typeOfOrganisation: '',
  yearEstablished: '',
  registrationNumber: '',
  categories: [],
  otherCategory: '',
  areaOfSpecialization: '',
  employeesTotal: '',
  employeesFullTime: '',
  employeesPartTime: '',
  authRepName: '',
  authRepTitle: '',
  authRepPhone: '',
  authRepEmail: '',
  accountName: '',
  bankName: '',
  bankAddress: '',
  accountNumber: '',
  iban: '',
  swiftBic: '',
  currency: '',
  auditedFinancials: '',
  turnoverCurrent: '',
  turnoverPrevious: '',
  bidSecurityAbility: '',
  bidSecurityType: '',
  complianceRepName: '',
  complianceRepNationality: '',
  complianceRepDOB: '',
  complianceRepPlaceOfBirth: '',
  complianceRepIdNumber: '',
  complianceRepCountryOfIssue: '',
  complianceRepFathersName: '',
  complianceRepMothersName: '',
  complianceRepAddress: '',
  complianceRepCountry: '',
  complianceRepPhone: '',
  complianceRepEmail: '',
  complianceRepPosition: '',
  confirmAccurate: false,
  agreeCodeOfConduct: false,
  understandFalseInfo: false,
  declarationName: '',
  declarationTitle: '',
  declarationDate: '',
  signature: ''
};
const companyDocuments: DocumentDef[] = [{
  key: 'incorporation',
  label: 'Certificate of Incorporation',
  required: true,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'businessRegistration',
  label: 'Business Registration Certificate',
  required: true,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'companyProfile',
  label: 'Company Profile',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'proofOfAddress',
  label: 'Proof of Registered Address',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'taxRegistration',
  label: 'Tax Registration Certificate',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}, {
  key: 'bankConfirmation',
  label: 'Bank Confirmation Letter',
  required: false,
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
}];
const identityDocuments: DocumentDef[] = [{
  key: 'governmentId',
  label: 'Government-Issued Identification (Passport / National ID)',
  required: true,
  accept: '.pdf,.jpg,.jpeg,.png'
}, {
  key: 'passportPhoto',
  label: 'Passport-Sized Photograph',
  required: true,
  accept: '.jpg,.jpeg,.png'
}];
const allDocuments = [...companyDocuments, ...identityDocuments];
const inputClass = 'w-full rounded-lg border border-gray-border bg-white px-4 py-2.5 text-charcoal focus:outline-none focus:ring-2 focus:ring-au-green focus:border-transparent';
function downloadBlankEOIForm() {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait'
  });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const margin = 18;
  let y = 34;
  const ensure = (space: number) => {
    if (y + space > height - 16) {
      doc.addPage();
      y = 20;
    }
  };
  const heading = (text: string) => {
    ensure(14);
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.5);
    doc.text(text, margin, y);
    doc.setDrawColor(252, 209, 22);
    doc.setLineWidth(0.8);
    doc.line(margin, y + 1.8, margin + 27, y + 1.8);
    y += 9;
  };
  const line = (label: string) => {
    ensure(9);
    doc.setTextColor(26, 31, 46);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(label, margin, y);
    const start = Math.min(margin + doc.getTextWidth(label) + 3, width - margin - 10);
    doc.setDrawColor(160, 160, 160);
    doc.setLineWidth(0.2);
    doc.line(start, y + 1, width - margin, y + 1);
    y += 9;
  };
  const check = (label: string) => {
    ensure(7);
    doc.setDrawColor(26, 31, 46);
    doc.rect(margin, y - 3.4, 3.6, 3.6);
    doc.setTextColor(26, 31, 46);
    doc.setFontSize(9.5);
    doc.text(label, margin + 6, y);
    y += 7;
  };
  doc.setFillColor(0, 104, 55);
  doc.rect(0, 0, width, 24, 'F');
  doc.setFillColor(252, 209, 22);
  doc.rect(0, 24, width, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('AFRICAN UNION COMMISSION', margin, 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('Procurement Department', margin, 17);
  doc.setTextColor(26, 31, 46);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('EXPRESSION OF INTEREST (EOI) FORM', margin, y);
  y += 10;
  heading('SECTION 1: GENERAL INFORMATION');
  ['1. Name of Company:', '1.1 Parent Company (if applicable):', '2. Full Address of the Company:', '3. Telephone Number (including country code):', '4. WhatsApp Number:', '5. Email Address:', '6. Name and Title of Contact Person:', '7. Type of Organisation:', '8. Year Established:', '9. Company Registration Number:'].forEach(line);
  heading('SECTION 2: BUSINESS INFORMATION');
  ['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent', 'Other'].forEach(check);
  ['Area of Specialization:', 'Number of Employees — Total / Full Time / Part Time:', 'Authorised Representative — Name:', 'Authorised Representative — Title:', 'Authorised Representative — Telephone:', 'Authorised Representative — Email:'].forEach(line);
  heading('SECTION 3: FINANCIAL & BANKING INFORMATION');
  ['Account Name:', 'Bank Name:', 'Bank Address:', 'Account Number:', 'IBAN:', 'SWIFT/BIC:', 'Currency of Account:', 'Audited Financial Statements Available:', 'Annual Turnover — Current Year:', 'Annual Turnover — Previous Year:'].forEach(line);
  heading('SECTION 4: COMPLIANCE & SECURITY REQUIREMENTS');
  ['Ability to provide Bid Security (Yes / No):', 'Type of Bid Security:', 'Full Legal Name:', 'Nationality:', 'Date of Birth:', 'Place of Birth:', 'National Identification / Passport Number:', 'Country of Issue:', "Father's Full Name:", "Mother's Full Name:", 'Residential / Business Address:', 'Country of Residence:', 'Mobile Telephone Number:', 'Email Address:', 'Current Occupation / Position:'].forEach(line);
  check('Government-Issued Identification attached');
  check('Passport-Sized Photograph attached');
  heading('DECLARATION');
  ['Name:', 'Title:', 'Date:', 'Signature:', 'Company Stamp / Seal:'].forEach(line);
  doc.save('AU-Expression-of-Interest-Form.pdf');
}
export function SupplierRegistration() {
  const screenInit = useScreenInit() as {
    currentStep?: number;
  };
  const initialStep = Math.min(5, Math.max(1, screenInit.currentStep ?? 1));
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [data, setData] = useState<FormData>(emptyForm);
  const [files, setFiles] = useState<Record<DocKey, File | null>>({
    incorporation: null,
    businessRegistration: null,
    companyProfile: null,
    proofOfAddress: null,
    taxRegistration: null,
    bankConfirmation: null,
    governmentId: null,
    passportPhoto: null
  });
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRefs = useRef<Record<DocKey, HTMLInputElement | null>>({
    incorporation: null,
    businessRegistration: null,
    companyProfile: null,
    proofOfAddress: null,
    taxRegistration: null,
    bankConfirmation: null,
    governmentId: null,
    passportPhoto: null
  });
  const steps = ['General Information', 'Business Information', 'Financial & Banking', 'Compliance & Security', 'Declaration & Submit'];
  const update = (key: TextKey, value: string) => {
    setData(previous => ({
      ...previous,
      [key]: value
    }));
  };
  const normalizePhone = (value: string) => {
    const compact = value.trim().replace(/[\s\-().]/g, '');
    if (compact.startsWith('00')) return `+${compact.slice(2)}`;
    if (!compact.startsWith('+') && /^\d{8,15}$/.test(compact)) return `+${compact}`;
    return compact;
  };
  const attachmentSize = () => Object.values(files).reduce((total, file) => total + (file?.size ?? 0), 0) / 1048576;
  const validateStep = () => {
    setError(null);
    if (currentStep === 1) {
      const required: [string, string][] = [[data.companyName, 'Name of Company'], [data.address, 'Full Address of the Company'], [data.email, 'Email Address'], [data.contactFullName, 'Name of Contact Person'], [data.contactTitle, 'Title of Contact Person'], [data.typeOfOrganisation, 'Type of Organisation'], [data.yearEstablished, 'Year Established'], [data.registrationNumber, 'Company Registration Number']];
      const missing = required.filter(([value]) => !value.trim()).map(([, label]) => label);
      const phone = normalizePhone(data.phone);
      if (!/^\+\d{8,15}$/.test(phone)) {
        setPhoneError('Enter a valid number in international format, for example +447700900123.');
        return false;
      }
      setPhoneError(null);
      if (phone !== data.phone) update('phone', phone);
      if (!data.whatsappNumber.trim()) {
        setWhatsappError('Please provide a WhatsApp number. If you do not use WhatsApp, enter your telephone number here instead.');
        return false;
      }
      setWhatsappError(null);
      if (missing.length) {
        setError(`Please complete: ${missing.join(', ')}.`);
        return false;
      }
    }
    if (currentStep === 2) {
      if (!data.categories.length) {
        setError('Please select at least one Business Category.');
        return false;
      }
      if (data.categories.includes('Other') && !data.otherCategory.trim()) {
        setError('Please specify the Other Business Category.');
        return false;
      }
      if (!data.areaOfSpecialization.trim()) {
        setError('Please provide the Area of Specialization.');
        return false;
      }
    }
    if (currentStep === 4) {
      const required: [string, string][] = [[data.bidSecurityAbility, 'Ability to provide Bid Security'], [data.bidSecurityType, 'Type of Bid Security'], [data.complianceRepName, 'Full Legal Name'], [data.complianceRepNationality, 'Nationality'], [data.complianceRepDOB, 'Date of Birth'], [data.complianceRepPlaceOfBirth, 'Place of Birth'], [data.complianceRepIdNumber, 'National ID / Passport Number'], [data.complianceRepCountryOfIssue, 'Country of Issue'], [data.complianceRepFathersName, "Father's Full Name"], [data.complianceRepMothersName, "Mother's Full Name"], [data.complianceRepAddress, 'Residential / Business Address'], [data.complianceRepCountry, 'Country of Residence'], [data.complianceRepPhone, 'Mobile Telephone Number'], [data.complianceRepEmail, 'Email Address'], [data.complianceRepPosition, 'Current Occupation / Position']];
      const missing = required.filter(([value]) => !value.trim()).map(([, label]) => label);
      const missingFiles = identityDocuments.filter(document => !files[document.key]).map(document => document.label);
      if (missing.length || missingFiles.length) {
        setError(`Compliance & Security is compulsory. Please complete: ${[...missing, ...missingFiles].join(', ')}.`);
        return false;
      }
    }
    return true;
  };
  const handleNext = () => {
    if (!validateStep()) return;
    setCurrentStep(step => Math.min(step + 1, steps.length));
  };
  const handleSubmit = async () => {
    setError(null);
    if (!data.confirmAccurate || !data.agreeCodeOfConduct || !data.understandFalseInfo) {
      setError('Please tick all three declaration checkboxes to proceed.');
      return;
    }
    if (!data.declarationName.trim() || !data.signature.trim()) {
      setError('Please provide your name and digital signature in the declaration.');
      return;
    }
    const missingDocs = allDocuments.filter(document => document.required && !files[document.key]).map(document => document.label);
    if (missingDocs.length) {
      setError(`Missing required documents: ${missingDocs.join(', ')}.`);
      return;
    }
    if (attachmentSize() > MAX_TOTAL_FILE_SIZE_MB) {
      setError(`Total attachment size exceeds ${MAX_TOTAL_FILE_SIZE_MB}MB. Please compress documents or photos and try again.`);
      return;
    }
    const payload = new FormData();
    payload.append('_subject', 'New AU Expression of Interest submission');
    payload.append('_captcha', 'false');
    payload.append('_template', 'table');
    const values: Array<[string, string]> = [['Company Name', data.companyName], ['Parent Company', data.parentCompany], ['Company Address', data.address], ['Telephone Number', normalizePhone(data.phone)], ['WhatsApp Number', data.whatsappNumber], ['Company Email', data.email], ['Contact Person', data.contactFullName], ['Contact Person Title', data.contactTitle], ['Type of Organisation', data.typeOfOrganisation], ['Year Established', data.yearEstablished], ['Company Registration Number', data.registrationNumber], ['Business Categories', data.categories.map(item => item === 'Other' ? `Other: ${data.otherCategory}` : item).join(', ')], ['Area of Specialization', data.areaOfSpecialization], ['Employees Total', data.employeesTotal], ['Employees Full Time', data.employeesFullTime], ['Employees Part Time', data.employeesPartTime], ['Authorised Representative Name', data.authRepName], ['Authorised Representative Title', data.authRepTitle], ['Authorised Representative Telephone', data.authRepPhone], ['Authorised Representative Email', data.authRepEmail], ['Bank Account Name', data.accountName], ['Bank Name', data.bankName], ['Bank Address', data.bankAddress], ['Bank Account Number', data.accountNumber], ['IBAN', data.iban], ['SWIFT / BIC', data.swiftBic], ['Account Currency', data.currency], ['Audited Financial Statements Available', data.auditedFinancials], ['Annual Turnover - Current Year', data.turnoverCurrent], ['Annual Turnover - Previous Year', data.turnoverPrevious], ['Ability to Provide Bid Security', data.bidSecurityAbility], ['Type of Bid Security', data.bidSecurityType], ['Compliance Representative - Full Legal Name', data.complianceRepName], ['Compliance Representative - Nationality', data.complianceRepNationality], ['Compliance Representative - Date of Birth', data.complianceRepDOB], ['Compliance Representative - Place of Birth', data.complianceRepPlaceOfBirth], ['Compliance Representative - ID / Passport Number', data.complianceRepIdNumber], ['Compliance Representative - Country of Issue', data.complianceRepCountryOfIssue], ["Compliance Representative - Father's Full Name", data.complianceRepFathersName], ["Compliance Representative - Mother's Full Name", data.complianceRepMothersName], ['Compliance Representative - Address', data.complianceRepAddress], ['Compliance Representative - Country of Residence', data.complianceRepCountry], ['Compliance Representative - Mobile Telephone', data.complianceRepPhone], ['Compliance Representative - Email', data.complianceRepEmail], ['Compliance Representative - Position', data.complianceRepPosition], ['Declaration', 'Confirmed accurate information | Agreed supplier code of conduct | Acknowledged false information consequences'], ['Declaration Name', data.declarationName], ['Declaration Title', data.declarationTitle], ['Declaration Date', data.declarationDate], ['Digital Signature', data.signature]];
    values.forEach(([name, value]) => payload.append(name, value));
    allDocuments.forEach(document => {
      const file = files[document.key];
      if (file) payload.append(document.label, file, file.name);
    });
    setIsSubmitting(true);
    try {
      const response = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: payload
      });
      if (!response.ok) {
        let message = 'There was a problem submitting your EOI. Please try again.';
        try {
          const result: {
            message?: string;
          } = await response.json();
          if (result.message) message = result.message;
        } catch {
          // Keep the user-friendly default message.
        }
        setError(message);
        return;
      }
      setSubmitted(true);
    } catch {
      setError('We could not connect to the submission service. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderTextInput = (id: string, label: string, key: TextKey, type = 'text', required = false) => <div data-id="element-856">
      <label htmlFor={id} className="block text-sm font-medium text-charcoal mb-2" data-id="element-857">
        {label} {required && <span className="text-au-red" data-id="element-858">*</span>}
      </label>
      <input id={id} type={type} value={data[key]} onChange={event => update(key, event.target.value)} className={inputClass} data-id="element-859" />
    </div>;
  const renderUpload = (document: DocumentDef) => {
    const file = files[document.key];
    return <div key={document.key} className={`border-2 border-dashed rounded-lg p-5 ${file ? 'border-au-green bg-au-green-light' : 'border-gray-border'}`} data-id="element-860">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-861">
          <div data-id="element-862">
            <p className="text-sm font-medium text-charcoal" data-id="element-863">
              {document.label}{' '}
              {document.required && <span className="text-au-red" data-id="element-864">*</span>}
            </p>
            {file && <p className="text-xs text-au-green mt-1" data-id="element-865">
                {file.name} · {(file.size / 1048576).toFixed(2)}MB
              </p>}
          </div>
          <div className="flex items-center gap-2" data-id="element-866">
            <input ref={element => {
            fileRefs.current[document.key] = element;
          }} id={`file-${document.key}`} type="file" accept={document.accept} onChange={event => setFiles(previous => ({
            ...previous,
            [document.key]: event.target.files?.[0] ?? null
          }))} className="sr-only" data-id="element-867" />
            <Button type="button" variant="secondary" onClick={() => fileRefs.current[document.key]?.click()} className="flex items-center gap-2 py-2 text-sm" data-id="element-868">
              <UploadIcon size={16} data-id="element-869" />
              {file ? 'Replace' : 'Choose File'}
            </Button>
            {file && <button type="button" onClick={() => {
            setFiles(previous => ({
              ...previous,
              [document.key]: null
            }));
            if (fileRefs.current[document.key]) {
              fileRefs.current[document.key]!.value = '';
            }
          }} className="rounded p-2 text-au-red hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-au-green" aria-label={`Remove ${document.label}`} data-id="element-870">
                <XCircleIcon size={20} data-id="element-871" />
              </button>}
          </div>
        </div>
      </div>;
  };
  if (submitted) {
    return <div className="w-full min-h-screen bg-ivory" data-id="element-872">
        <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'Procurement'
      }, {
        label: 'Expression of Interest'
      }]} data-id="element-873" />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center" data-id="element-874">
          <Card data-id="element-875">
            <div className="w-20 h-20 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-876">
              <CheckCircleIcon size={48} className="text-au-green" data-id="element-877" />
            </div>
            <h1 className="text-3xl font-serif text-charcoal mb-3" data-id="element-878">
              Expression of Interest Submitted
            </h1>
            <div className="w-16 h-1 bg-au-gold mx-auto mb-6" data-id="element-879" />
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-880">
              Thank you for submitting your Expression of Interest to the
              African Union Commission. Your submission and supporting documents
              have been received for review.
            </p>
            <Button variant="secondary" onClick={() => window.location.href = '/'} data-id="element-881">
              Return to Home
            </Button>
          </Card>
        </main>
      </div>;
  }
  return <div className="w-full min-h-screen bg-ivory" data-id="element-882">
      <PageHero title="Expression of Interest (EOI)" subtitle="Submit an Expression of Interest to participate in AU procurement opportunities" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Expression of Interest'
    }]} data-id="element-883" />

      <main className="max-w-4xl mx-auto px-4 py-12" data-id="element-884">
        <section className="mb-8 rounded-lg border border-au-green/20 bg-au-green-light/50 p-6" data-id="element-885">
          <h1 className="text-xl font-serif text-charcoal mb-3" data-id="element-886">
            Expression of Interest (EOI) Form
          </h1>
          <p className="text-sm leading-relaxed text-gray-700" data-id="element-887">
            Complete all required information accurately and provide the
            requested supporting documentation for the African Union Commission
            supplier engagement process.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-888">
            <Button type="button" variant="secondary" onClick={downloadBlankEOIForm} className="flex items-center gap-2 text-sm" data-id="element-889">
              <DownloadIcon size={16} data-id="element-890" />
              Download Blank EOI Form (PDF)
            </Button>
            <span className="text-xs text-gray-600" data-id="element-891">
              Download a printable working copy before completing the online
              form.
            </span>
          </div>
        </section>

        <FormStepper currentStep={currentStep} steps={steps} data-id="element-892" />

        <Card data-id="element-893">
          {currentStep === 1 && <section aria-labelledby="general-heading" data-id="element-894">
              <h2 id="general-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-895">
                SECTION 1: GENERAL INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-896" />
              <div className="space-y-5" data-id="element-897">
                {renderTextInput('company-name', '1. Name of Company', 'companyName', 'text', true)}
                {renderTextInput('parent-company', '1.1 Parent Company (if applicable)', 'parentCompany')}
                <div data-id="element-898">
                  <label htmlFor="company-address" className="block text-sm font-medium text-charcoal mb-2" data-id="element-899">
                    2. Full Address of the Company{' '}
                    <span className="text-au-red" data-id="element-900">*</span>
                  </label>
                  <textarea id="company-address" rows={3} value={data.address} onChange={event => update('address', event.target.value)} className={inputClass} data-id="element-901" />
                </div>
                <div className="grid md:grid-cols-2 gap-5" data-id="element-902">
                  <div data-id="element-903">
                    <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-2" data-id="element-904">
                      3. Telephone Number <span className="text-au-red" data-id="element-905">*</span>
                    </label>
                    <input id="phone" type="tel" value={data.phone} onChange={event => update('phone', event.target.value)} onBlur={() => {
                  const phone = normalizePhone(data.phone);
                  if (/^\+\d{8,15}$/.test(phone)) {
                    update('phone', phone);
                    setPhoneError(null);
                  }
                }} placeholder="+447700900123" aria-invalid={Boolean(phoneError)} className={inputClass} data-id="element-906" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-907">
                      Use international format beginning with + and country
                      code.
                    </p>
                    {phoneError && <p role="alert" className="mt-1 text-xs text-au-red" data-id="element-908">
                        {phoneError}
                      </p>}
                  </div>
                  <div data-id="element-909">
                    <label htmlFor="whatsapp" className="block text-sm font-medium text-charcoal mb-2" data-id="element-910">
                      4. WhatsApp Number <span className="text-au-red" data-id="element-911">*</span>
                    </label>
                    <input id="whatsapp" type="tel" value={data.whatsappNumber} onChange={event => {
                  update('whatsappNumber', event.target.value);
                  setWhatsappError(null);
                }} placeholder="+447700900123" aria-invalid={Boolean(whatsappError)} className={inputClass} data-id="element-912" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-913">
                      If you do not use WhatsApp, enter your telephone number
                      here instead.
                    </p>
                    {whatsappError && <p role="alert" className="mt-1 text-xs text-au-red" data-id="element-914">
                        {whatsappError}
                      </p>}
                  </div>
                </div>
                {renderTextInput('email', '5. Email Address', 'email', 'email', true)}
                <div className="grid md:grid-cols-2 gap-5" data-id="element-915">
                  {renderTextInput('contact-name', '6. Name of Contact Person', 'contactFullName', 'text', true)}
                  {renderTextInput('contact-title', 'Title of Contact Person', 'contactTitle', 'text', true)}
                </div>
                <div data-id="element-916">
                  <label htmlFor="organisation-type" className="block text-sm font-medium text-charcoal mb-2" data-id="element-917">
                    7. Type of Organisation{' '}
                    <span className="text-au-red" data-id="element-918">*</span>
                  </label>
                  <select id="organisation-type" value={data.typeOfOrganisation} onChange={event => update('typeOfOrganisation', event.target.value)} className={inputClass} data-id="element-919">
                    <option value="" data-id="element-920">Select type</option>
                    <option value="Private Company" data-id="element-921">Private Company</option>
                    <option value="State Enterprise" data-id="element-922">State Enterprise</option>
                    <option value="Other" data-id="element-923">Other</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-5" data-id="element-924">
                  {renderTextInput('year-established', '8. Year Established', 'yearEstablished', 'text', true)}
                  {renderTextInput('registration-number', '9. Company Registration Number', 'registrationNumber', 'text', true)}
                </div>
              </div>
            </section>}

          {currentStep === 2 && <section aria-labelledby="business-heading" data-id="element-925">
              <h2 id="business-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-926">
                SECTION 2: BUSINESS INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-927" />
              <div className="space-y-6" data-id="element-928">
                <fieldset data-id="element-929">
                  <legend className="text-sm font-medium text-charcoal mb-3" data-id="element-930">
                    10. Business Category <span className="text-au-red" data-id="element-931">*</span>
                  </legend>
                  <div className="grid md:grid-cols-2 gap-3" data-id="element-932">
                    {['Manufacturer', 'Trading Company', 'Service Provider', 'Contractor', 'Technology Company', 'Supplier', 'Authorized Agent', 'Other'].map(category => <label key={category} className="flex items-center gap-2 rounded-lg border border-gray-border p-3 cursor-pointer" data-id="element-933">
                        <input type="checkbox" checked={data.categories.includes(category)} onChange={event => setData(previous => ({
                    ...previous,
                    categories: event.target.checked ? [...previous.categories, category] : previous.categories.filter(item => item !== category),
                    otherCategory: category === 'Other' && !event.target.checked ? '' : previous.otherCategory
                  }))} className="w-4 h-4 text-au-green focus:ring-au-green" data-id="element-934" />
                        <span className="text-sm text-charcoal" data-id="element-935">
                          {category}
                        </span>
                      </label>)}
                  </div>
                  {data.categories.includes('Other') && <input type="text" value={data.otherCategory} onChange={event => update('otherCategory', event.target.value)} className={`${inputClass} mt-3`} placeholder="Please specify the other category" aria-label="Specify other business category" data-id="element-936" />}
                </fieldset>
                <div data-id="element-937">
                  <label htmlFor="specialization" className="block text-sm font-medium text-charcoal mb-2" data-id="element-938">
                    11. Area of Specialization{' '}
                    <span className="text-au-red" data-id="element-939">*</span>
                  </label>
                  <textarea id="specialization" rows={3} value={data.areaOfSpecialization} onChange={event => update('areaOfSpecialization', event.target.value)} className={inputClass} data-id="element-940" />
                </div>
                <div className="grid md:grid-cols-3 gap-4" data-id="element-941">
                  {renderTextInput('employee-total', '12. Employees — Total', 'employeesTotal', 'number')}
                  {renderTextInput('employee-full-time', 'Full Time', 'employeesFullTime', 'number')}
                  {renderTextInput('employee-part-time', 'Part Time', 'employeesPartTime', 'number')}
                </div>
                <fieldset className="border-t border-gray-border pt-6" data-id="element-942">
                  <legend className="text-sm font-medium text-charcoal mb-4" data-id="element-943">
                    13. Authorised Representative
                  </legend>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-944">
                    {renderTextInput('auth-name', 'Name', 'authRepName')}
                    {renderTextInput('auth-title', 'Title', 'authRepTitle')}
                    {renderTextInput('auth-phone', 'Telephone', 'authRepPhone', 'tel')}
                    {renderTextInput('auth-email', 'Email', 'authRepEmail', 'email')}
                  </div>
                </fieldset>
              </div>
            </section>}

          {currentStep === 3 && <section aria-labelledby="financial-heading" data-id="element-945">
              <h2 id="financial-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-946">
                SECTION 3: FINANCIAL &amp; BANKING INFORMATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-947" />
              <div className="space-y-5" data-id="element-948">
                <div className="grid md:grid-cols-2 gap-4" data-id="element-949">
                  {renderTextInput('account-name', 'Account Name', 'accountName')}
                  {renderTextInput('bank-name', 'Bank Name', 'bankName')}
                </div>
                {renderTextInput('bank-address', 'Bank Address', 'bankAddress')}
                <div className="grid md:grid-cols-2 gap-4" data-id="element-950">
                  {renderTextInput('account-number', 'Account Number', 'accountNumber')}
                  {renderTextInput('iban', 'IBAN', 'iban')}
                  {renderTextInput('swift-bic', 'SWIFT / BIC', 'swiftBic')}
                  {renderTextInput('currency', 'Currency of Account', 'currency')}
                </div>
                <fieldset data-id="element-951">
                  <legend className="text-sm font-medium text-charcoal mb-2" data-id="element-952">
                    14. Audited Financial Statements Available
                  </legend>
                  <div className="flex gap-5" data-id="element-953">
                    {['Yes', 'No'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer" data-id="element-954">
                        <input type="radio" name="audited" value={option} checked={data.auditedFinancials === option} onChange={event => update('auditedFinancials', event.target.value)} className="w-4 h-4 text-au-green" data-id="element-955" />
                        <span className="text-sm" data-id="element-956">{option}</span>
                      </label>)}
                  </div>
                </fieldset>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-957">
                  {renderTextInput('turnover-current', '15. Annual Turnover — Current Year', 'turnoverCurrent')}
                  {renderTextInput('turnover-previous', 'Annual Turnover — Previous Year', 'turnoverPrevious')}
                </div>
                <section className="border-t border-gray-border pt-6" aria-labelledby="company-documents-heading" data-id="element-958">
                  <h3 id="company-documents-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-959">
                    16. Registration Documents
                  </h3>
                  <p className="mb-5 text-sm text-gray-600" data-id="element-960">
                    Maximum combined upload size:{' '}
                    <strong data-id="element-961">{MAX_TOTAL_FILE_SIZE_MB}MB</strong>. Compress PDFs
                    and photos if required.
                  </p>
                  <div className="space-y-4" data-id="element-962">
                    {companyDocuments.map(renderUpload)}
                  </div>
                  {attachmentSize() > MAX_TOTAL_FILE_SIZE_MB && <div role="alert" className="mt-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-id="element-963">
                      <AlertCircleIcon size={18} className="shrink-0 mt-0.5" data-id="element-964" />
                      <span data-id="element-965">
                        Total attachment size exceeds {MAX_TOTAL_FILE_SIZE_MB}
                        MB. Please compress documents or photos.
                      </span>
                    </div>}
                </section>
              </div>
            </section>}

          {currentStep === 4 && <section aria-labelledby="compliance-heading" data-id="element-966">
              <h2 id="compliance-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-967">
                SECTION 4: COMPLIANCE &amp; SECURITY REQUIREMENTS{' '}
                <span className="text-au-red" data-id="element-968">(COMPULSORY)</span>
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-969" />
              <div className="space-y-7" data-id="element-970">
                <div className="rounded-lg border border-au-green/20 bg-au-green-light/50 p-5 text-sm leading-relaxed text-gray-800" data-id="element-971">
                  <strong data-id="element-972">This section is mandatory.</strong> All bidders and
                  suppliers are required to complete it fully. Failure to
                  provide the required information may result in the application
                  not being considered.
                </div>
                <fieldset data-id="element-973">
                  <legend className="text-lg font-bold text-charcoal mb-3" data-id="element-974">
                    1. Bid Security
                  </legend>
                  <div className="space-y-4" data-id="element-975">
                    <div data-id="element-976">
                      <span className="block text-sm font-medium text-charcoal mb-2" data-id="element-977">
                        Ability to provide Bid Security{' '}
                        <span className="text-au-red" data-id="element-978">*</span>
                      </span>
                      <div className="flex gap-5" data-id="element-979">
                        {['Yes', 'No'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer" data-id="element-980">
                            <input type="radio" name="security" value={option} checked={data.bidSecurityAbility === option} onChange={event => update('bidSecurityAbility', event.target.value)} className="w-4 h-4 text-au-green" data-id="element-981" />
                            <span className="text-sm" data-id="element-982">{option}</span>
                          </label>)}
                      </div>
                    </div>
                    {renderTextInput('security-type', 'Type of Bid Security', 'bidSecurityType', 'text', true)}
                  </div>
                </fieldset>

                <section className="border-t border-gray-border pt-6" aria-labelledby="representative-heading" data-id="element-983">
                  <h3 id="representative-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-984">
                    2. Authorised Representative Verification
                  </h3>
                  <p className="mb-5 text-sm text-gray-600" data-id="element-985">
                    This information is required for identity verification and
                    compliance purposes.
                  </p>
                  <div className="grid md:grid-cols-2 gap-5" data-id="element-986">
                    {renderTextInput('rep-name', 'Full Legal Name', 'complianceRepName', 'text', true)}
                    {renderTextInput('rep-nationality', 'Nationality', 'complianceRepNationality', 'text', true)}
                    {renderTextInput('rep-dob', 'Date of Birth', 'complianceRepDOB', 'date', true)}
                    {renderTextInput('rep-birthplace', 'Place of Birth', 'complianceRepPlaceOfBirth', 'text', true)}
                    {renderTextInput('rep-id', 'National Identification / Passport Number', 'complianceRepIdNumber', 'text', true)}
                    <div data-id="element-987">
                      <label htmlFor="issue-country" className="block text-sm font-medium text-charcoal mb-2" data-id="element-988">
                        Country of Issue <span className="text-au-red" data-id="element-989">*</span>
                      </label>
                      <select id="issue-country" value={data.complianceRepCountryOfIssue} onChange={event => update('complianceRepCountryOfIssue', event.target.value)} className={inputClass} data-id="element-990">
                        <option value="" data-id="element-991">Select country</option>
                        {countries.map(country => <option key={country.code} value={country.name} data-id="element-992">
                            {country.name}
                          </option>)}
                      </select>
                    </div>
                    {renderTextInput('rep-father', "Father's Full Name", 'complianceRepFathersName', 'text', true)}
                    {renderTextInput('rep-mother', "Mother's Full Name", 'complianceRepMothersName', 'text', true)}
                    <div className="md:col-span-2" data-id="element-993">
                      {renderTextInput('rep-address', 'Residential / Business Address', 'complianceRepAddress', 'text', true)}
                    </div>
                    {renderTextInput('rep-residence', 'Country of Residence', 'complianceRepCountry', 'text', true)}
                    {renderTextInput('rep-position', 'Current Occupation / Position', 'complianceRepPosition', 'text', true)}
                    {renderTextInput('rep-phone', 'Mobile Telephone Number', 'complianceRepPhone', 'tel', true)}
                    {renderTextInput('rep-email', 'Email Address', 'complianceRepEmail', 'email', true)}
                  </div>
                </section>

                <section className="border-t border-gray-border pt-6" aria-labelledby="identity-heading" data-id="element-994">
                  <h3 id="identity-heading" className="text-lg font-bold text-charcoal mb-2" data-id="element-995">
                    3. Identity Documents{' '}
                    <span className="text-au-red" data-id="element-996">(Mandatory)</span>
                  </h3>
                  <p className="mb-5 text-sm text-gray-600" data-id="element-997">
                    Upload a clear government-issued identification document and
                    a recent passport-sized photograph.
                  </p>
                  <div className="space-y-4" data-id="element-998">
                    {identityDocuments.map(renderUpload)}
                  </div>
                </section>
              </div>
            </section>}

          {currentStep === 5 && <section aria-labelledby="declaration-heading" data-id="element-999">
              <h2 id="declaration-heading" className="text-2xl font-bold text-charcoal mb-2" data-id="element-1000">
                DECLARATION
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-1001" />
              <div className="space-y-6" data-id="element-1002">
                <div className="rounded-lg border border-au-green/20 bg-au-green-light/50 p-6 text-sm leading-relaxed text-gray-800" data-id="element-1003">
                  I hereby certify that the information provided in this
                  Expression of Interest form and all supporting documentation
                  is true, complete and accurate. I understand that this
                  Expression of Interest does not constitute a binding contract
                  and that supplier approval is subject to the African Union
                  Commission&apos;s evaluation and procurement procedures.
                </div>
                <fieldset className="space-y-3" data-id="element-1004">
                  <legend className="sr-only" data-id="element-1005">Required declarations</legend>
                  {[['confirmAccurate', 'I confirm that all information provided is accurate and complete.'], ['agreeCodeOfConduct', 'I agree to the African Union Supplier Code of Conduct.'], ['understandFalseInfo', 'I understand that providing false information may result in disqualification.']].map(([key, label]) => <label key={key} className="flex items-start gap-3 cursor-pointer" data-id="element-1006">
                      <input type="checkbox" checked={data[key as keyof Pick<FormData, 'confirmAccurate' | 'agreeCodeOfConduct' | 'understandFalseInfo'>]} onChange={event => setData(previous => ({
                  ...previous,
                  [key]: event.target.checked
                }))} className="mt-1 w-4 h-4 text-au-green focus:ring-au-green" data-id="element-1007" />
                      <span className="text-sm text-gray-700" data-id="element-1008">{label}</span>
                    </label>)}
                </fieldset>
                <div className="grid md:grid-cols-2 gap-5" data-id="element-1009">
                  {renderTextInput('declaration-name', 'Name', 'declarationName', 'text', true)}
                  {renderTextInput('declaration-title', 'Title', 'declarationTitle')}
                  {renderTextInput('declaration-date', 'Date', 'declarationDate', 'date')}
                  {renderTextInput('signature', 'Signature', 'signature', 'text', true)}
                </div>
                {error && <div role="alert" className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-1010">
                    <AlertCircleIcon size={18} className="shrink-0 mt-0.5" data-id="element-1011" />
                    <span data-id="element-1012">{error}</span>
                  </div>}
              </div>
            </section>}

          {currentStep !== 5 && error && <div role="alert" className="mt-6 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-1013">
              <AlertCircleIcon size={18} className="shrink-0 mt-0.5" data-id="element-1014" />
              <span data-id="element-1015">{error}</span>
            </div>}

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-gray-border pt-6" data-id="element-1016">
            <Button type="button" variant="secondary" onClick={() => {
            setError(null);
            setCurrentStep(step => Math.max(1, step - 1));
          }} disabled={currentStep === 1 || isSubmitting} data-id="element-1017">
              Previous
            </Button>
            {currentStep < steps.length ? <Button type="button" onClick={handleNext} data-id="element-1018">
                Next Step
              </Button> : <div className="sm:text-right" data-id="element-1019">
                <Button type="button" onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-1020">
                  {isSubmitting ? 'Submitting…' : <>
                      <CheckCircleIcon size={20} data-id="element-1021" />
                      Submit Expression of Interest
                    </>}
                </Button>
                <p className="mt-3 max-w-md text-xs text-gray-500 sm:ml-auto" role="note" data-id="element-1022">
                  Administrative note: The first live test submission requires
                  the procurement inbox to confirm FormSubmit activation once.
                </p>
              </div>}
          </div>
        </Card>
      </main>
    </div>;
}